import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { usePageMeta } from '@/hooks/usePageMeta'

const partners = [
  {
    id: 'frce',
    name: 'First Responder Center for Excellence',
    logo: '/spoke-static/images/SPOKE_frce_logo.jpg',
    logoBg: true,
    logoSize: 'h-36 md:h-52',
    url: 'https://firstrespondercenter.org/',
    urlLabel: 'firstrespondercenter.org',
    body: `The First Responder Center for Excellence (FRCE), an affiliate of the National Fallen Firefighters Foundation, is dedicated to protecting the lives and livelihoods of firefighters, EMS personnel, and other first responders through research, education, and training that reduce line-of-duty injuries, deaths, and occupational illnesses.

Partnering with FRCE allows SPOKE to extend our film's message into real-world action by bringing powerful, story-driven content alongside FRCE's evidence-based resources, helping reach new audiences, spark conversation, and serve as a catalyst for culture change around first responder health and wellness.`,
  },
  {
    id: 'frsr',
    name: 'First Responder Sleep Recovery',
    logo: '/spoke-static/images/frsr-logo-new.png',
    logoBg: true,
    logoSize: 'h-36 md:h-48',
    url: 'https://www.firstrespondersleeprecovery.com/band',
    urlLabel: 'firstrespondersleeprecovery.com',
    body: `We are proud to have sleep and stress specialist Jacqueline Toomey as a partner. Jacqueline is the founder of First Responder Sleep Recovery: a firefighter-owned training program that tackles the life-threatening impact of sleep loss on first responders.`,
  },
  {
    id: 'rrfa',
    name: 'Renton Regional Fire Authority',
    logo: '/spoke-static/images/SPOKE_renton_fire_authority_logo.png',
    logoBg: false,
    logoSize: 'h-36 md:h-52',
    url: null,
    urlLabel: null,
    body: `We're proud to partner with the Renton Regional Fire Authority (RRFA), whose dedicated F13 crew opened their doors to us during filming. Their teamwork, skill, and commitment to community safety embody the spirit of frontline service that our film celebrates.

Through our collaboration with RRFA, audiences gain a genuine look into the lives of the firefighters who protect and serve with courage every day.`,
  },
  {
    id: 'crackyl',
    name: 'CRACKYL Magazine',
    logo: '/spoke-static/images/crackyl-logo.png',
    logoBg: false,
    logoSize: 'h-16 md:h-24',
    url: 'https://www.crackyl.com/',
    urlLabel: 'crackyl.com',
    body: `CRACKYL Magazine is a firefighter-focused lifestyle and wellness publication dedicated to supporting the physical, mental, and emotional health of firefighters and their families.

As SPOKE's media partner, CRACKYL helps amplify important conversations around resilience, fitness, relationships, and life beyond the fireground, bringing our message directly into firehouses and firefighter communities across North America.`,
  },
  {
    id: 'filmabee',
    name: 'Filmabee Studios',
    logo: '/spoke-static/images/SPOKE_filmabee_studios_logo_transparent.png',
    logoBg: false,
    logoSize: 'h-56 md:h-60',
    url: 'https://filmabee.com',
    urlLabel: 'filmabee.com',
    body: `SPOKE is a production of Filmabee Studios, and it sits right at the heart of the company's vision for cinema: intimate, human-centered stories that stay with you long after the credits roll.

By following real people through a descent "into maydays no one is talking about," SPOKE reflects Filmabee's belief that cinema can be a map for an inspired world, drawn from the real struggles and quiet courage of the people on screen. As a studio that "works with visionary film directors and their audience," Filmabee uses projects like SPOKE to turn lived experience into crafted, cinematic storytelling that sparks reflection and conversation, not just momentary spectacle.`,
  },
]

const PartnersPage = () => {
  usePageMeta({
    title: 'Partners | SPOKE Film',
    description: 'Community partners and supporters helping bring the SPOKE firefighter documentary to life and audiences worldwide.',
  })
  return (
    <>
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[38vh] bg-[#0a0f1a] flex items-end pt-24 pb-16 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-[#0a0f1a]"></div>
        <div className="relative z-10 max-w-5xl mx-auto w-full text-center">
          <div className="w-12 h-1 bg-red-600 mb-6 mx-auto"></div>
          <h1 className="text-5xl md:text-7xl font-montserrat font-black uppercase tracking-wide text-white leading-none">
            Partners
          </h1>
          <p className="mt-4 text-yellow-400 font-poppins font-light text-lg">
            The organisations and people who make{' '}
            <span className="font-montserrat font-black uppercase tracking-widest text-white">SPOKE</span>
            {' '}possible.
          </p>
        </div>
      </section>

      {/* Alternating partner sections */}
      {partners.map((partner, i) => {
        const isEven = i % 2 === 0

        const LogoInner = partner.logoBg ? (
          <div className="bg-white rounded-2xl p-6 inline-flex items-center justify-center transition-transform duration-200 hover:scale-110">
            <img
              src={partner.logo}
              alt={partner.name}
              className={`${partner.logoSize} w-auto object-contain`}
            />
          </div>
        ) : (
          <img
            src={partner.logo}
            alt={partner.name}
            className={`${partner.logoSize} w-auto object-contain transition-transform duration-200 hover:scale-110`}
          />
        )

        const LogoBlock = (
          <div className="flex items-center justify-center">
            {partner.url ? (
              <a href={partner.url} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                {LogoInner}
              </a>
            ) : (
              LogoInner
            )}
          </div>
        )

        const TextBlock = (
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-8 h-[2px] ${isEven ? 'bg-red-600' : 'bg-yellow-400'}`}></div>
              <h2 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">
                {partner.name}
              </h2>
            </div>
            {partner.body.split('\n\n').map((para, j) => (
              <p key={j} className="text-gray-300 font-poppins font-light text-base leading-relaxed mb-4">
                {para}
              </p>
            ))}
            {partner.url && (
              <a
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 text-yellow-400 hover:text-yellow-300 font-montserrat font-black uppercase tracking-widest text-xs transition-colors duration-200"
              >
                Find Out More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            )}
          </div>
        )

        return (
          <section
            key={partner.id}
            className={`py-10 md:py-20 px-6 ${isEven ? 'bg-black' : 'bg-[#0a0f1a]'}`}
          >
            <div className="max-w-5xl mx-auto">
              {/* Desktop: alternating left/right */}
              <div className={`hidden md:grid grid-cols-2 gap-16 items-center ${isEven ? '' : 'direction-rtl'}`}>
                {isEven ? (
                  <>
                    {LogoBlock}
                    {TextBlock}
                  </>
                ) : (
                  <>
                    {TextBlock}
                    {LogoBlock}
                  </>
                )}
              </div>

              {/* Mobile/tablet: logo then text stacked */}
              <div className="md:hidden flex flex-col gap-5">
                {LogoBlock}
                {TextBlock}
              </div>
            </div>
          </section>
        )
      })}

      <Footer />
    </>
  )
}

export default PartnersPage
